import {
  all, call, fork, put, select, takeEvery, takeLatest, take, race, delay
} from 'redux-saga/effects';
import { push } from '../../utils/customRouter';
import { PageActionTypes } from './types';
import SessionHelper from '../../utils/sessionHelper';
import merge from 'lodash.merge';
import {
  updateNodeActiveState,
  updateNodeActiveStateSuccess,
  pageRequest,
  pageSuccess,
  mergeStyleSuccess,
  clearForms,
  formValuesSuccess,
  resetFormValuesSuccess,
  updateVariableSuccess,
  uncheckDependentRadioSuccess,
  formValuesRequest,
  activateLocalShimmerSuccess,
  actionSuccess,
} from './actions';
import { v4 as v4uuid } from 'uuid';
import { configRequest } from '../config/actions';
import apiRequest from '../../utils/apiReq';
import { 
  setItem as setLocalCache, 
  getItem as getLocalCache,
  getItemRegexp as getLocalCacheRegexp,
  removeItem as removeFromLocalCache
 } from '../../utils/indexedDbHelper';
import { 
  recursiveUpdateById, 
  recursivelyUpdateActiveState,
  reccursivelyUncheckRadioItems,
  reccursivelyUncheckRadioItemsLegacy,
  getDeviceInfo, 
  parseFormValues, 
  replacePlaceholdersWithVariables,
  matchesPattern,
  formShimmerCacheKey,
  transformShimmerCache,
  mergeLocalVariablesIntoGlobal,
  parseOldFormat,
  parseNewFormat,
  updateDataWithModeUpdate,
  sessionDataTransformer,
 } from '../../utils/base';


 function styleMerger(page: any, updates: any) {
  const inArr = Array.isArray(page) ? page : [page];
    const updatesMap = updates.reduce((map, update) => {
    map[update.object_id] = update;
    return map;
  }, {} as Record<string, any>);
  const updatedComponents = recursiveUpdateById(inArr, updatesMap, (item) => {
    const update = Object.keys(updatesMap).find((objId) => 
      objId === item.id || (matchesPattern(objId, item.id) && updatesMap[objId]?.object_type === item.component)
    );

    if (update) {
      return {
        ...item,
        activeState: updatesMap[update]?.active_state || item.activeState,
        style: merge({}, item.style, updatesMap[update]?.style),
      };
    }
    return item;
  });
  return updatedComponents;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handlePageRequest(action: any) {
  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { location, silent, componentId } = payload || {};
  const { pathname: pathnameDraft, search } = location || {};

  const pathname = (pathnameDraft === '/') ? '/' : pathnameDraft.replace(/\/$/, '');

  // Correct the route if it ends with a trailing slash without triggering a location change
  if (pathnameDraft.length > 1 && pathnameDraft.endsWith('/')) {
    const correctedPathname = pathnameDraft.slice(0, -1);
    const newUrl = search ? `${correctedPathname}${search}` : correctedPathname;
    window.history.replaceState(null, '', newUrl);
  }

  try {
    const breakLoadPages = ['/not_found', '/frame/internal'];
    if (pathname && !breakLoadPages.includes(pathname)) {
      const page = (pathname === '/' || pathname === '') ? '/' : pathname.replace('/', '');
      yield put(clearForms());

      const cacheKey = formShimmerCacheKey({ command: `${page}${search?.length ? search : ''}`, action: undefined, component_id: componentId, screen_id: screenId });
      const [key, value] = cacheKey ? yield call(getLocalCacheRegexp, cacheKey) : [null, null];

      if (!/\{\{[^}]+\}\}/.test(key) && !silent) {
        try {
          const currentPath = window.location.pathname === '/' ? '/' : window.location.pathname.replace(/\/$/, '');
          const currentSearch = window.location.search;
          const navigateTo = search ? `${pathname}${search}` : pathname;
                
          if (currentPath !== pathname || 
              (search && currentSearch !== search)) {
            // yield put(push(navigateTo));
            if ((window.location.pathname + window.location.search) !== navigateTo) {
              console.log('Pushing to history:', componentId, navigateTo);
              window.history.pushState(null, '', navigateTo);
            }
          }
        } catch (navError) {
          console.error('Navigation failed:', navError);
          // Continue execution even if navigation fails
        }
      }

     if (value && !silent) {
        const { page: pp, settings, variableData, screenId: screenIdParsed } = value.sections ? parseOldFormat(value) : parseNewFormat(value);
        let mergedData;
        if (localShimmer?.length) {
          mergedData = styleMerger(pp, localShimmer);
        }
        yield put(pageSuccess({
          data: mergedData || pp, 
          settings, 
          session_data: {
            type: 'indexedDb',
            localShimmer: localShimmer?.length ? true : false,
            hash: v4uuid(),
          }, 
          variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
          screenId: screenIdParsed,
         }));
      } else if (localShimmer?.length) {
        yield put(activateLocalShimmerSuccess());
      }

      const { code, data, session_data } = yield call(
        apiRequest,
        'post',
        '/get',
        {
          command: `${page}${search?.length ? search : ''}`,
          skip_cache: silent ? true : !!value,
          screen_id: screenId,
          active_route: `${window.location.pathname}${window.location.search || ''}`,
          component_id: componentId,
          device_info: getDeviceInfo(),
          session_id: SessionHelper.getOrCreateSessionId(),
        },
      );
      if (code !== 200 || !data) return;
      const { page: pp, settings, variableData, screenId: screenIdParsed } = data.sections ? parseOldFormat(data) : parseNewFormat(data);

      // Handle new update mode scheme
      let finalPageData = pp;
      let finalVariableData = variableData;
      let shouldUpdateCache = false;
      
      if (data.mode === 'update') {
        const currentPageData = yield select((state) => state.page.data.page);
        const currentVariableData = yield select((state) => state.page.variableData);
        
        const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
          updateDataWithModeUpdate(currentPageData, data, currentVariableData);
        
        finalPageData = updatedPage;
        finalVariableData = updatedVariableData;
        shouldUpdateCache = cacheFlag;
      }

      yield put(pageSuccess({ 
        data: finalPageData, 
        settings, 
        session_data: {
          ...sessionDataTransformer(session_data),
          localShimmer: false,
          hash: (value && !silent) ? `skip-${v4uuid()}` : v4uuid(),
        }, 
        variableData: mergeLocalVariablesIntoGlobal(finalVariableData, settings?.variables),
        screenId: screenIdParsed,
       }));

       const { status, transformed } = transformShimmerCache(data?.screen?.sections || []);

      // Handle caching based on mode and settings
      if (data.mode === 'update') {
        // For update mode, save the final transformed data
        if (shouldUpdateCache) {
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: finalPageData,
            },
            variable_data: finalVariableData,
          });
        }
      } else {
        if (settings?.command && settings?.cache) {
          setLocalCache(settings.command, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });

          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });
        } else if (status && transformed && !silent && settings?.cache) {
          // Update response with transformed sections
          // Save
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });
        } else if (!componentId && !screenId && !silent && settings?.cache) {
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: data?.screen?.sections,
            },
            variable_data: variableData,
          });
        }
      }
      
      // else if (!silent) {
      //   removeFromLocalCache(cacheKey);
      // }

          // Handle settings.command URL update
      if (settings?.command) {
        const newCommand = settings.command.replace(/^\//, '').replace(/\/$/, '');
        const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;
        
        if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
          // yield put(push(`/${newCommand}`));
          if (window.location.pathname + window.location.search !== `/${newCommand}`) {
            console.log('Pushing to history:', componentId, `/${newCommand}`);
            window.history.pushState(null, '', `/${newCommand}`);
          }
        }
      }

      // Parse form values
      const formValues = parseFormValues(finalPageData);
      yield put(resetFormValuesSuccess({ data: formValues }));
    }
  } catch (error) {
    if (error instanceof Error) {
      console.info('Error in handlePageRequest:', error.message);
    }
  }
}

function* handleSetFormValuesRequest(action: any) {
  const { payload } = action || {};
  const { field } = payload || {};
  const { id, value } = field || {};

  yield put(formValuesSuccess({ data: { [id]: value } }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Helper function to convert value to specified type
const convertToType = (value: any, type?: string): any => {
  if (!type || value === null || value === undefined) return value;
  
  switch (type.toLowerCase()) {
    case 'string':
      return String(value);
    case 'number':
      const num = Number(value);
      return isNaN(num) ? String(value) : num;
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
      }
      return Boolean(value);
    case 'array':
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch {
          return [value];
        }
      }
      return [value];
    case 'object':
      if (typeof value === 'object') return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' && parsed !== null ? parsed : { value };
        } catch {
          return { value };
        }
      }
      return { value };
    default:
      return String(value);
  }
};

function* sendAllFormValues(action: any) {
  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { value, delay: del, componentId, block, silent } = payload || {};
  // Stop processing if block is true
  if (block) {
    return;
  }
  const page = yield select((state) => state.page);
  const { settings, variableData, formValues } = page || {};
  const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
  const replacedFormValue = replacePlaceholdersWithVariables(formValues, mergedVariables);

  yield delay(del|| 0);

  const cacheKey = formShimmerCacheKey({ command: undefined, action: value, component_id: componentId, screen_id: screenId });
  const [key2, value2] = cacheKey ? yield call(getLocalCache, cacheKey) : [null, null];

if (value2 && !silent) {
    const { page: pp, settings, variableData, screenId: screenIdParsed } = value2.sections ? parseOldFormat(value2) : parseNewFormat(value2);
            let mergedData;
        if (localShimmer?.length) {
          mergedData = styleMerger(pp, localShimmer);
        }
    yield put(pageSuccess({ 
      data: mergedData || pp,
      settings,
      session_data: {
        localShimmer: localShimmer?.length ? true : false,
        type: 'indexedDb',
        hash: `skip-${v4uuid()}`,
      },
      variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
      screenId: screenIdParsed,
     }));
  }  else if (localShimmer?.length) {
      yield put(activateLocalShimmerSuccess());
  }

  try {
    yield put(clearForms());
    const { code, data, session_data } = yield call(
      apiRequest,
      'post',
      '/get',
      {
        action: value,
        screen_id: screenId,
        skip_cache: silent ? true : !!value,
        active_route: `${window.location.pathname}${window.location.search || ''}`,
        component_id: componentId,
        device_info: getDeviceInfo(),
        session_id: SessionHelper.getOrCreateSessionId(),
        ...replacedFormValue,
      },
    );
    if (code !== 200 || !data) return;
    const { page: pp, settings, variableData, screenId: screenIdParsed } = data.sections ? parseOldFormat(data) : parseNewFormat(data);
    
    // Handle new update mode scheme
    let finalPageData = pp;
    let finalVariableData = variableData;
    let shouldUpdateCache = false;
    
    if (data.mode === 'update') {
      const currentPageData = yield select((state) => state.page.data.page);
      const currentVariableData = yield select((state) => state.page.variableData);
      
      const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
        updateDataWithModeUpdate(currentPageData, data, currentVariableData);
      
      finalPageData = updatedPage;
      finalVariableData = updatedVariableData;
      shouldUpdateCache = cacheFlag;
    }
    
    yield put(pageSuccess({ 
      data: finalPageData, 
      settings, 
      session_data: {
        ...sessionDataTransformer(session_data),
        localShimmer: false,
        hash: `skip-${v4uuid()}`,
      }, 
      variableData: mergeLocalVariablesIntoGlobal(finalVariableData, settings?.variables),
      screenId: screenIdParsed,
     }));

     const { status, transformed } = transformShimmerCache(data?.screen?.sections || []);

    // Handle caching based on mode and settings
    if (data.mode === 'update') {
      // For update mode, save the final transformed data
      if (shouldUpdateCache) {
        setLocalCache(cacheKey, {
          ...data,
          screen: {
            ...data.screen,
            sections: finalPageData,
          },
          variable_data: finalVariableData,
        });
      }
    } else {
      if (status && transformed && !silent && settings?.cache) {
        setLocalCache(cacheKey, {
          ...data,
          screen: {
            ...data.screen,
            sections: transformed,
          },
          variable_data: variableData,
        });
      } else if (!silent) {
        if (cacheKey) removeFromLocalCache(cacheKey);
      }
    } 

    // Handle settings.command URL update
    if (settings?.command) {
      const newCommand = settings.command.replace(/^\//, '').replace(/\/$/, '');
      const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;
      
      if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
        // yield put(push(`/${newCommand}`));
        if (window.location.pathname + window.location.search !== `/${newCommand}`) {
          console.log('Pushing to history:', componentId, `/${newCommand}`);
          window.history.pushState(null, '', `/${newCommand}`);
        }
      }
    }

    // Parse form values
    const formValues = parseFormValues(finalPageData);
    yield put(resetFormValuesSuccess({ data: formValues }));
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error in sendAllFormValues:', error.message);
    }
  }
}

function* updateVariableRequest(action: any) {
  const { payload } = action || {};
  const { variable, value } = payload || {};
  const keys = variable.split('.');
  const result = keys.reduceRight((acc, key) => ({
    [key]: acc
  }), value);

  yield put(updateVariableSuccess({ data: result }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleLocationChange(action: any) {
  const { location, isFirstRendering } = action.payload;
  if (isFirstRendering === true) {
    // Check if config is already initialized from init script
    const configState = yield select((state: any) => state.config);
    const isConfigFromInitScript = configState?.initializedFromInitScript === true;
    
    if (isConfigFromInitScript) {
      console.log('Config already loaded from init script, skipping config/page request');
      // yield put(pageRequest({ ...action.payload }));
    } else {
      yield put(configRequest(location));
    }
  } else {
    yield put(pageRequest({ ...action.payload }));
  }
}

function* handleMergeStyle(action: any) {
  const { payload, meta } = action || {};
  const { context, index } = meta || {};
  const updates = Array.isArray(payload) ? payload : [payload];
  const { page } = yield select((state) => state.page.data);
  const styleChunks = yield select((state) => state.page.styleChunks);
  const { indexes, refs } = styleChunks || {};

  // Helper function to extract context values and generate ref keys
  const generateRefKey = (objectId: string, context: any) => {
    if (!context || !objectId.includes('{{')) return null;
    
    let refKey = objectId;
    
    // Match patterns like {{this.id}}, {{demo}}, {{demo.key}}
    const matches = objectId.match(/\{\{([^}]+)\}\}/g);
    
    if (matches) {
      matches.forEach(match => {
        const path = match.slice(2, -2); // Remove {{ and }}
        let value;
        
        // Handle {{this.}} patterns - extract from current context
        if (path.startsWith('this.')) {
          const contextPath = path.substring(5); // Remove 'this.'
          value = getNestedValue(context, contextPath);
        } else if (path.startsWith('this[')) {
          const contextPath = path.substring(4); // Remove 'this'
          value = getNestedValue(context, contextPath);
        } else {
          // Handle other patterns like {{demo}}, {{demo.key}}
          value = getNestedValue(context, path);
        }
        
        if (value !== undefined) {
          refKey = refKey.replace(match, value);
        }
      });
    }
    
    return refKey !== objectId ? refKey : null;
  };

  // Helper function to get nested value from object
  const getNestedValue = (obj: any, path: string) => {
    if (!path || obj === null || obj === undefined) return undefined;
    
    // Parse the path to handle both dot notation and bracket notation
    const parsePath = (pathStr: string): string[] => {
      const keys: string[] = [];
      let current = '';
      let inBrackets = false;
      let bracketDepth = 0;
      
      for (let i = 0; i < pathStr.length; i++) {
        const char = pathStr[i];
        
        if (char === '[') {
          if (inBrackets) {
            bracketDepth++;
            current += char;
          } else {
            if (current) {
              keys.push(current);
              current = '';
            }
            inBrackets = true;
          }
        } else if (char === ']') {
          if (bracketDepth > 0) {
            bracketDepth--;
            current += char;
          } else {
            inBrackets = false;
            if (current) {
              // Handle nested brackets and evaluate the content
              let key = current;
              
              // If the key contains quotes, remove them
              if ((key.startsWith('"') && key.endsWith('"')) || 
                  (key.startsWith("'") && key.endsWith("'"))) {
                key = key.slice(1, -1);
              }
              
              // If the key is a number, keep it as is
              // If it contains nested brackets or dots, it might be a complex expression
              keys.push(key);
              current = '';
            }
          }
        } else if (char === '.' && !inBrackets) {
          if (current) {
            keys.push(current);
            current = '';
          }
        } else {
          current += char;
        }
      }
      
      if (current) {
        keys.push(current);
      }
      
      return keys;
    };
    
    const keys = parsePath(path);
    
    return keys.reduce((current, key) => {
      if (current === null || current === undefined) return undefined;
      
      // Handle numeric keys for arrays
      if (!isNaN(Number(key))) {
        const numKey = Number(key);
        return Array.isArray(current) ? current[numKey] : current[key];
      }
      
      // Handle regular object keys
      return current[key];
    }, obj);
  };

  // Generate all ref updates at top level FIRST
  updates.forEach(update => {
    const refKey = generateRefKey(update.object_id, context);
    
    if (refKey && update.style) {
      if (!refs[refKey]) {
        refs[refKey] = {};
      }
      // Save refs with type and update structure
      refs[refKey] = merge({}, refs[refKey], {
        type: update.object_type,
        update: update?.style || {},
      });
    }
  });


  const updatesMap = updates.reduce((map, update) => {
    map[update.object_id] = update;
    return map;
  }, {} as Record<string, any>);

  const inArr = Array.isArray(page) ? page : [page];
  const updatedComponents = recursiveUpdateById(inArr, updatesMap, (item, insideTable) => {
    const update = Object.keys(updatesMap).find((objId) => 
      objId === item.id || (matchesPattern(objId, item.id) && updatesMap[objId]?.object_type === item.component)
    );

    if (update) {
      const updateData = updatesMap[update];
      
      if (insideTable && typeof index === 'number') {
        if (!indexes[item.id]) {
          indexes[item.id] = {};
        }
        indexes[item.id][index] = merge({}, indexes[item.id][index], updateData?.style);
        return item;
      } else {
        return {
          ...item,
          activeState: updateData?.active_state || item.activeState,
          style: merge({}, item.style, updateData?.style),
        };
      }
    }
    return item;
  });

  yield put(mergeStyleSuccess({ 
    data: updatedComponents,
    styleChunks: { indexes, refs } // Include updated refs
  }));
}

function* handleNodeActiveState(action: any) {
  const { payload } = action || {};
  const { id } = payload || {};
  const { page } = yield select((state) => state.page.data);
  const updatedComponents = recursivelyUpdateActiveState(page, id);

  yield put(updateNodeActiveStateSuccess({ data: updatedComponents }));
}

function* handleUncheck(action: any) {
  const { payload } = action || {};
  const { name, excludeId, originalName } = payload || {};
  const { page } = yield select((state) => state.page.data);
  const hasVariables = /\{\{.*?\}\}/.test(originalName);
  if (hasVariables) {
    // const { items, changedIds } = reccursivelyUncheckRadioItems(page, name, originalName, excludeId);

    // yield put(uncheckDependentRadioSuccess({ data: items }));

    // for (const item of changedIds) {
    //   yield put(formValuesRequest({ field: { id: item, value: false } }));
    // }
  } else {
    const { items, changedIds } = reccursivelyUncheckRadioItemsLegacy(page, name, excludeId);

    yield put(uncheckDependentRadioSuccess({ data: items }));

    for (const item of changedIds) {
      yield put(formValuesRequest({ field: { id: item, value: false } }));
    }

  }

}

function* watchNodeActiveStatePending() {
  yield takeEvery(PageActionTypes.UPDATE_NODE_ACTIVE_STATE_REQUEST, handleNodeActiveState);
}

function* watchLocationChange() {
  yield takeEvery(PageActionTypes.LOCATION_CHANGE, handleLocationChange);
}

// Helper function to merge payloads by object_id while preserving context/index
const mergePayloadsByContext = (batches: any, newPayload: any, newMeta: any) => {
  const contextKey = `${newMeta?.context ? JSON.stringify(newMeta.context) : 'null'}_${newMeta?.index ?? 'null'}`;
  
  if (!batches[contextKey]) {
    batches[contextKey] = {
      meta: newMeta,
      payloads: []
    };
  }
  
  const currentBatch = batches[contextKey];
  const payloadArray = Array.isArray(newPayload) ? newPayload : [newPayload];
  
  payloadArray.forEach((payload: any) => {
    const existingIndex = currentBatch.payloads.findIndex((item: any) => item.object_id === payload.object_id);
    if (existingIndex !== -1) {
      // Merge styles for existing object_id
      currentBatch.payloads[existingIndex] = {
        ...currentBatch.payloads[existingIndex],
        ...payload,
        style: merge({}, currentBatch.payloads[existingIndex].style, payload.style),
      };
    } else {
      currentBatch.payloads.push(payload);
    }
  });
};

// Batch MERGE_STYLE_REQUEST actions within 45ms, preserving context and index
function* mergeStyleBatcher() {
  let batches: Record<string, { meta: any; payloads: any[] }> = {}; // Group by context + index
  
  while (true) {
    // Wait for the first MERGE_STYLE_REQUEST
    const firstAction = yield take(PageActionTypes.MERGE_STYLE_REQUEST);
    mergePayloadsByContext(batches, firstAction.payload, firstAction.meta);

    // Now, for 45ms, collect any more MERGE_STYLE_REQUESTs
    while (true) {
      const { nextAction, timeout } = yield race({
        nextAction: take(PageActionTypes.MERGE_STYLE_REQUEST),
        timeout: delay(50),
      });
      
      if (nextAction) {
        mergePayloadsByContext(batches, nextAction.payload, nextAction.meta);
      } else {
        
        for (const [contextKey, batch] of Object.entries(batches)) {
          yield call(handleMergeStyle, { 
            payload: batch.payloads, 
            meta: batch.meta 
          });
        }
        
        batches = {};
        break;
      }
    }
  }
}

function* handleActionRequest(action: any) {
  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { actionUpdate, componentId } = payload || {};
  const { type, method, to, format, data, target, silent, delay: actionDelay, settings, } = actionUpdate?.action || {};

  const { send_all_parameters: sendAll } = settings || {};

  // Apply delay if specified
  if (actionDelay && actionDelay > 0) {
    yield delay(actionDelay * 1000);
  }

  try {
    if (type === 'request') {
      const page = yield select((state) => state.page);
      const { settings, variableData, formValues: currentFormValues } = page || {};
      const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
      const replacedFormValue = replacePlaceholdersWithVariables(currentFormValues, mergedVariables);
      
      // Replace placeholders in URL
      const urlWithVariables = replacePlaceholdersWithVariables(to, mergedVariables);
      
      // Transform data: extract actual values from form storage based on data keys
      let additionalData = {};
      if (data && typeof data === 'object') {
        additionalData = Object.keys(data).reduce((acc, key) => {
          const fieldConfig = data[key];
          let extractedValue;
          let targetType: string | undefined;
          
          // Check if there's a direct value in replacedFormValue
          if (replacedFormValue.hasOwnProperty(key)) {
            extractedValue = replacedFormValue[key];
            // If fieldConfig is an object, it might have a type
            if (typeof fieldConfig === 'object' && fieldConfig !== null && 'type' in fieldConfig) {
              targetType = fieldConfig.type;
            }
          } 
          // Check if fieldConfig contains a placeholder pattern like {{variable}}
          else if (typeof fieldConfig === 'string') {
            const variableMatch = fieldConfig.match(/\{\{([^}]+)\}\}/);
            if (variableMatch) {
              const variableName = variableMatch[1].trim();
              extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(fieldConfig, mergedVariables);
            }
          }
          // Check if fieldConfig is an object with a value property
          else if (typeof fieldConfig === 'object' && fieldConfig !== null && 'value' in fieldConfig) {
            const value = fieldConfig.value;
            const valueOriginal = fieldConfig.valueOriginal;
            targetType = fieldConfig.type;
            
            if (typeof valueOriginal === 'string' && /\{\{[^}]+\}\}/.test(valueOriginal)) {
              const variableMatch = valueOriginal.match(/\{\{([^}]+)\}\}/);
              if (variableMatch) {
                const variableName = variableMatch[1].trim();
                extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(valueOriginal, mergedVariables);
              }
            } else {
              extractedValue = value;
            }
          }
          
          // Convert to target type if specified
          if (extractedValue !== undefined) {
            acc[key] = convertToType(extractedValue, targetType);
          }
          
          return acc;
        }, {} as Record<string, any>);
      }

      const cacheKey = formShimmerCacheKey({ command: undefined, action: urlWithVariables, component_id: componentId, screen_id: screenId });
      const [key2, value2] = cacheKey ? yield call(getLocalCache, cacheKey) : [null, null];

      if (value2 && !silent) {
        const { page: pp, settings, variableData, screenId: screenIdParsed } = value2.sections ? parseOldFormat(value2) : parseNewFormat(value2);
        let mergedData;
        if (localShimmer?.length) {
          mergedData = styleMerger(pp, localShimmer);
        }
        yield put(pageSuccess({ 
          data: mergedData || pp,
          settings,
          session_data: {
            localShimmer: localShimmer?.length ? true : false,
            type: 'indexedDb',
            hash: `skip-${v4uuid()}`,
          },
          variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
          screenId: screenIdParsed,
        }));
      } else if (localShimmer?.length) {
        yield put(activateLocalShimmerSuccess());
      }
      
      if (!silent) {
        console.log(`Making ${method || 'POST'} request to ${urlWithVariables}`, { ...replacedFormValue, ...additionalData });
      }

      yield put(clearForms());
      
      // Prepare request data based on sendAll setting
      const requestData = sendAll ? {
        screen_id: screenId,
        skip_cache: silent ? true : !!value2,
        active_route: `${window.location.pathname}${window.location.search || ''}`,
        component_id: componentId,
        device_info: getDeviceInfo(),
        session_id: SessionHelper.getOrCreateSessionId(),
        ...replacedFormValue,
        ...additionalData,
      } : {
        ...additionalData,
      };

      // Make direct request to the specified URL (not using apiRequest wrapper)
      const requestHeaders: Record<string, string> = {
        Accept: 'application/json',
      };

      // Add Fractal headers if making request to same origin
      const isSameOrigin = urlWithVariables.startsWith('/') || urlWithVariables.includes(window.location.host);
      if (isSameOrigin) {
        requestHeaders['Fc'] = 'web'; // Fractal-Client
        requestHeaders['Fie'] = 'true'; // Fractal-Interface-Engine
        requestHeaders['Fsi'] = sessionStorage.getItem('sessionId') || ''; // Fractal-Session-ID
        requestHeaders['Fdi'] = localStorage.getItem('deviceId') || ''; // Fractal-Device-ID
      }

      let finalUrl = urlWithVariables;
      let requestBody: string | BodyInit | undefined;

      // For GET requests, append data as query parameters
      if (method?.toUpperCase() === 'GET') {
        const urlObj = new URL(urlWithVariables, window.location.origin);
        
        // Add all request data as query parameters
        Object.entries(requestData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            urlObj.searchParams.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
        
        finalUrl = urlObj.toString();
      } else {
        // For non-GET requests, use body based on format
        const isFormFormat = format === 'form';
        
        if (isFormFormat) {
          // Use form-encoded format
          requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
          const formBody = new URLSearchParams();
          Object.entries(requestData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formBody.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
            }
          });
          requestBody = formBody.toString();
        } else {
          // Default to JSON format
          requestHeaders['Content-Type'] = 'application/json';
          requestBody = JSON.stringify(requestData);
        }
      }

      const response = yield call(fetch, finalUrl, {
        method: method?.toUpperCase() || 'POST',
        headers: requestHeaders,
        ...(requestBody && { body: requestBody }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseJson = yield call([response, 'json']);
      const code = response.status;
      const responseData = responseJson.data || responseJson;
      const session_data = sessionDataTransformer(responseJson.session_data || {});

      if (code !== 200 || !responseData) {
        yield put(actionSuccess({ success: false, error: 'Invalid response' }));
        return;
      }

      const { page: pp, settings: responseSettingsDraft, variableData: responseVariableData, screenId: screenIdParsed } = responseData.sections ? parseOldFormat(responseData) : parseNewFormat(responseData);
      
      // Handle new update mode scheme
      let finalPageData = pp;
      let finalVariableData = responseVariableData;
      let responseSettings = responseSettingsDraft;
      let shouldUpdateCache = false;
      
      if (responseData.mode === 'update') {
        const currentPageData = yield select((state) => state.page.data.page);
        const currentVariableData = yield select((state) => state.page.variableData);
        
        const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
          updateDataWithModeUpdate(currentPageData, responseData, currentVariableData);
        
        responseSettings = merge({}, settings, responseSettingsDraft);
        finalPageData = updatedPage;
        finalVariableData = updatedVariableData;
        shouldUpdateCache = cacheFlag;
      }
      
      yield put(pageSuccess({ 
        data: finalPageData, 
        settings: responseSettings, 
        session_data: {
          ...session_data,
          localShimmer: false,
          hash: `skip-${v4uuid()}`,
        }, 
        variableData: mergeLocalVariablesIntoGlobal(finalVariableData, responseSettings?.variables),
        screenId: screenIdParsed,
      }));

      const { status, transformed } = transformShimmerCache(responseData?.screen?.sections || []);

      // Handle caching based on mode and settings
      if (responseData.mode === 'update') {
        // For update mode, save the final transformed data
        if (shouldUpdateCache) {
          setLocalCache(cacheKey, {
            ...responseData,
            screen: {
              ...responseData.screen,
              sections: finalPageData,
            },
            variable_data: finalVariableData,
          });
        }
      } else {
        if (status && transformed && !silent && responseSettings?.cache) {
          setLocalCache(cacheKey, {
            ...responseData,
            screen: {
              ...responseData.screen,
              sections: transformed,
            },
            variable_data: responseVariableData,
          });
        } else if (!silent) {
          if (cacheKey) removeFromLocalCache(cacheKey);
        }
      }

      // Handle settings.command URL update
      if (responseSettings?.command) {
        const newCommand = responseSettings.command.replace(/^\//, '').replace(/\/$/, '');
        const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;
        
        if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
          if (window.location.pathname + window.location.search !== `/${newCommand}`) {
            console.log('Pushing to history:', componentId, `/${newCommand}`);
            window.history.pushState(null, '', `/${newCommand}`);
          }
        }
      }

      // Parse form values
      const formValues = parseFormValues(finalPageData);
      yield put(resetFormValuesSuccess({ data: formValues }));
      
      if (!silent) {
        console.log('Request successful:', responseData);
      }

      yield put(actionSuccess({ success: true, data: responseData }));
    } else if (type === 'url') {
      // Get variable data for placeholder replacement
      const variableData = yield select((state) => state.page.variableData);
      const settings = yield select((state) => state.page.settings);
      const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
      
      // Replace placeholders in URL
      const urlWithVariables = replacePlaceholdersWithVariables(to, mergedVariables);
      
      if (target === '_blank') {
        window.open(urlWithVariables, '_blank');
      } else {
        window.location.href = urlWithVariables;
      }

      yield put(actionSuccess({ success: true, type: 'url' }));
    }
  } catch (error) {
    if (!silent) {
      console.error('Action request failed:', error);
    }
    yield put(actionSuccess({ success: false, error: error.message }));
  }
}

function* watchFetchRequest() {
  yield takeLatest(PageActionTypes.PAGE_REQUEST, handlePageRequest);
  yield fork(mergeStyleBatcher);
}

function* watchHandleInputRequest() {
  yield takeEvery(PageActionTypes.SET_FORM_VALUES_REQUEST, handleSetFormValuesRequest);
}

function* watchSubmitFormRequest() {
  yield takeLatest(PageActionTypes.SUBMIT_FORM_REQUEST, sendAllFormValues);
}

function* watchUpdateVariableRequest() {
  yield takeLatest(PageActionTypes.UPDATE_VARIABLE_REQUEST, updateVariableRequest);
}

function* watchActionRequest() {
  yield takeLatest(PageActionTypes.ACTION_REQUEST, handleActionRequest);
}

function* watchUncheckDependent() {
  yield takeEvery(PageActionTypes.UNCHECK_DEPENDENT_RADIO_REQUEST, handleUncheck);
}

function* pageSaga() {
  yield all([
    fork(watchNodeActiveStatePending),
    fork(watchHandleInputRequest),
    fork(watchFetchRequest),
    fork(watchSubmitFormRequest),
    fork(watchLocationChange),
    fork(watchUpdateVariableRequest),
    fork(watchActionRequest),
    fork(watchUncheckDependent),
  ]);
}

export default pageSaga;